import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import DataTable from "../components/common/Table";
import Modal, { FIELD_CLASS, LABEL_CLASS } from "../components/common/Modal";
import ConfirmModal from "../components/common/ConfirmModal";
import useStore from "../store/useStore";
import api from "../services/api";

const emptyForm = {
  name: "",
  description: "",
  monthlyPrice: "",
  yearlyPrice: "",
  currency: "GMD",
  features: "",
  featured: false,
  sortOrder: "0",
  active: true
};

const SuperAdminSubscriptions = () => {
  const { toast } = useStore();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/subscription-plans");
      setPlans(res.data.data || []);
    } catch (error) {
      console.error("Load subscription plans error:", error);
      toast(error.response?.data?.message || "Failed to load subscription plans", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const closeForm = () => {
    setForm(emptyForm);
    setSelectedPlan(null);
    setFormOpen(false);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setSelectedPlan(null);
    setFormOpen(true);
  };

  const openEdit = (plan) => {
    setSelectedPlan(plan);
    setForm({
      name: plan.name || "",
      description: plan.description || "",
      monthlyPrice: plan.monthlyPrice?.toString() || "",
      yearlyPrice: plan.yearlyPrice?.toString() || "",
      currency: plan.currency || "GMD",
      features: (plan.features || []).join("\n"),
      featured: Boolean(plan.featured),
      sortOrder: plan.sortOrder?.toString() || "0",
      active: plan.active !== false
    });
    setFormOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const payloadFromForm = () => ({
    name: form.name.trim(),
    description: form.description.trim(),
    monthlyPrice: Number(form.monthlyPrice),
    yearlyPrice: Number(form.yearlyPrice),
    currency: form.currency,
    features: form.features,
    featured: form.featured,
    sortOrder: Number(form.sortOrder || 0),
    active: form.active
  });

  const handleAdd = async () => {
    if (!form.name.trim()) {
      toast("Plan name is required", "error");
      return;
    }

    if (Number(form.monthlyPrice) < 0 || Number(form.yearlyPrice) < 0 || form.monthlyPrice === "" || form.yearlyPrice === "") {
      toast("Monthly and yearly prices are required", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await api.post("/subscription-plans", payloadFromForm());
      toast(res.data?.message || "Subscription plan created", "success");
      closeForm();
      fetchPlans();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to create subscription plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPlan) return;

    if (!form.name.trim()) {
      toast("Plan name is required", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await api.put(`/subscription-plans/${selectedPlan.id}`, payloadFromForm());
      toast(res.data?.message || "Subscription plan updated", "success");
      closeForm();
      fetchPlans();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to update subscription plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      await api.delete(`/subscription-plans/${deleteTarget.id}`);
      toast("Subscription plan deleted", "success");
      setDeleteTarget(null);
      fetchPlans();
    } catch (error) {
      toast(error.response?.data?.message || "Failed to delete subscription plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = plans.filter((plan) => {
    const term = search.toLowerCase();
    return (
      (plan.name || "").toLowerCase().includes(term) ||
      (plan.description || "").toLowerCase().includes(term)
    );
  });

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        action={
          <Button variant="primary" icon="add" onClick={openCreate}>
            Add Plan
          </Button>
        }
      />

      <DataTable
        columns={[
          { key: "name", label: "Plan" },
          { key: "description", label: "Description", render: (row) => row.description || "-" },
          {
            key: "monthlyPrice",
            label: "Monthly (GMD)",
            sortValue: (row) => Number(row.monthlyPrice || 0),
            render: (row) => Number(row.monthlyPrice || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })
          },
          {
            key: "yearlyPrice",
            label: "Yearly (GMD)",
            sortValue: (row) => Number(row.yearlyPrice || 0),
            render: (row) => Number(row.yearlyPrice || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })
          },
          {
            key: "featured",
            label: "Popular",
            render: (row) => (row.featured ? "Yes" : "No")
          },
          {
            key: "active",
            label: "Status",
            render: (row) => (
              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                {row.active ? "Active" : "Inactive"}
              </span>
            )
          }
        ]}
        rows={filtered}
        loading={loading && !formOpen && !deleteTarget}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchPlaceholder="Search plans..."
        onEdit={openEdit}
        onDelete={setDeleteTarget}
        emptyMessage='No subscription plans yet. Click "Add Plan" to create one.'
      />

      <Modal
        open={formOpen}
        title={selectedPlan ? "Update Subscription Plan" : "Add Subscription Plan"}
        onClose={closeForm}
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={loading}>
              Cancel
            </Button>
            {selectedPlan ? (
              <Button variant="success" icon="update" onClick={handleUpdate} disabled={loading}>
                Update
              </Button>
            ) : (
              <Button variant="primary" icon="add" onClick={handleAdd} disabled={loading}>
                Add
              </Button>
            )}
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={LABEL_CLASS}>
              Plan Name <span className="text-red-500">*</span>
            </label>
            <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Starter" className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>Display Order</label>
            <input type="number" name="sortOrder" value={form.sortOrder} onChange={handleChange} min="0" className={FIELD_CLASS} />
          </div>
          <div className="md:col-span-2">
            <label className={LABEL_CLASS}>Description</label>
            <input type="text" name="description" value={form.description} onChange={handleChange} placeholder="Who this plan is for" className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>
              Monthly Price (GMD) <span className="text-red-500">*</span>
            </label>
            <input type="number" name="monthlyPrice" value={form.monthlyPrice} onChange={handleChange} min="0" step="0.01" className={FIELD_CLASS} />
          </div>
          <div>
            <label className={LABEL_CLASS}>
              Yearly Price (GMD) <span className="text-red-500">*</span>
            </label>
            <input type="number" name="yearlyPrice" value={form.yearlyPrice} onChange={handleChange} min="0" step="0.01" className={FIELD_CLASS} />
          </div>
          <div className="md:col-span-2">
            <label className={LABEL_CLASS}>Features (one per line)</label>
            <textarea
              name="features"
              value={form.features}
              onChange={handleChange}
              rows={5}
              placeholder={"Up to 10 properties\nTenant and lease records"}
              className={`${FIELD_CLASS} resize-none`}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
            <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="w-4 h-4" />
            Mark as popular
          </label>
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
            <input type="checkbox" name="active" checked={form.active} onChange={handleChange} className="w-4 h-4" />
            Active on public pricing
          </label>
        </div>
      </Modal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        message={deleteTarget ? `Delete subscription plan "${deleteTarget.name}"? If a company is using it, deactivate the plan instead.` : ""}
        loading={loading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default SuperAdminSubscriptions;
